import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Select,
  MenuItem,
  Button,
  Avatar,
  Chip,
  TablePagination,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import LinkIcon from "@mui/icons-material/Link";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import { useSnackbar } from "notistack";
import performRequest from "network/perform-request";
import UserContext from "context/UserContext";
import AssignMemberModal from "./AssignMemberModal";
import { debounce } from "lodash";
import {
  postAssignUserToMemberRoute,
  postCreateAndAssignUserToMemberRoute,
  getOrganizationUnlinkedParticipantsRoute,
  postAssignMemberToPlayerRoute,
  postCreateAndAssignMemberToPlayerRoute,
} from "network/api-routes";

const UnlinkedPlayersPage = () => {
  const { user, selectedOrganization } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();

  const [players, setPlayers] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [availableCompetitions, setAvailableCompetitions] = useState([]);
  const [selectedCompetition, setSelectedCompetition] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [exporting, setExporting] = useState(false);

  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchPlayers = () => {
    performRequest(
      "GET",
      getOrganizationUnlinkedParticipantsRoute,
      { organization_id: selectedOrganization.id, year, search },
      true,
      user,
    )
      .then((res) => {
        setPlayers(res.data.data ?? []);
        setAvailableYears(res.data.available_years ?? [new Date().getFullYear()]);
        setAvailableCompetitions(res.data.available_competitions ?? []);
        setPage(0);
      })
      .catch(() => enqueueSnackbar("No se pudo obtener la lista de jugadores", { variant: "error" }));
  };

  useEffect(() => {
    if (selectedOrganization) fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization, year, search]);

  const debouncedSearch = useMemo(
    () =>
      debounce((val) => {
        setSearch(val);
      }, 300),
    [],
  );

  useEffect(() => {
    debouncedSearch(inputValue);
    return () => {
      debouncedSearch.cancel();
    };
  }, [inputValue, debouncedSearch]);

  const handleActionClick = (player) => {
    setSelectedPlayer(player);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedPlayer(null);
  };

  const filteredAndSortedPlayers = useMemo(() => {
    let result = [...players];
    if (selectedCompetition !== "all") {
      result = result.filter((p) => p.competition_name === selectedCompetition);
    }

    result.sort((a, b) => {
      const aHas = a.suggestions && a.suggestions.length > 0 ? 1 : 0;
      const bHas = b.suggestions && b.suggestions.length > 0 ? 1 : 0;
      return bHas - aHas;
    });

    return result;
  }, [players, selectedCompetition]);

  const paginatedPlayers = useMemo(() => {
    return filteredAndSortedPlayers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredAndSortedPlayers, page, rowsPerPage]);

  const handleMemberSave = (data) => {
    console.log("Data recibida del modal:", data);
    if (data.action === "link") {
      const payload = {
        organization_member_id: data.member_id,
        player_id: data.player_id,
        participant_id: data.participant_id,
        ...(data.user_id ? { user_id: data.user_id } : {}),
      };

      const route = data.user_id ? postAssignUserToMemberRoute : postAssignMemberToPlayerRoute;

      performRequest("POST", route, payload, true, user)
        .then(() => {
          handleModalClose();
          fetchPlayers();
          enqueueSnackbar("Jugador vinculado correctamente", { variant: "success" });
        })
        .catch(() => enqueueSnackbar("Error al vincular jugador", { variant: "error" }));
    }

    if (data.action === "create") {
      const payload = {
        organization_id: selectedOrganization.id,
        name: data.name,
        document_number: data.document_number,
        cellphone_number: data.cellphone_number,
        is_active: data.is_active,
        ...(data.user_id ? { user_id: data.user_id } : { player_id: data.player_id }),
        participant_id: data.participant_id,
      };

      const route = data.user_id ? postCreateAndAssignUserToMemberRoute : postCreateAndAssignMemberToPlayerRoute;

      performRequest("POST", route, payload, true, user)
        .then((res) => {
          fetchPlayers();
          enqueueSnackbar(`Miembro creado y vinculado correctamente`, { variant: "success" });
        })
        .catch(() => enqueueSnackbar("Error al crear o vincular jugador", { variant: "error" }));
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await performRequest(
        "GET",
        getOrganizationUnlinkedParticipantsRoute,
        {
          organization_id: selectedOrganization.id,
          year,
          search,
        },
        true,
        user,
      );

      const data = res.data.data ?? [];

      if (!data.length) {
        enqueueSnackbar("No hay jugadores para exportar", { variant: "info" });
        return;
      }

      const headers = ["Evento", "Torneo", "Nombre", "Documento", "Teléfono", "Tipo"];

      const rows = data.map((p) => [
        p.competition_name ?? "-",
        p.tournament_name ?? "-",
        p.name,
        p.document_number ?? "-",
        p.cellphone_number ?? "-",
        p.playerable_type === "App\\User" ? "REVA" : "Manual",
      ]);

      const csvContent = [headers, ...rows].map((row) => row.map((field) => `"${field ?? ""}"`).join(",")).join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `jugadores_no_vinculados_${year}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      enqueueSnackbar("Error al exportar los jugadores", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Jugadores No Vinculados
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Revisa y resuelve los registros de jugadores huérfanos de importaciones recientes.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <Select size="small" value={year} onChange={(e) => setYear(e.target.value)} sx={{ minWidth: 120 }}>
            {availableYears.map((y) => (
              <MenuItem key={y} value={y}>
                Temporada {y}
              </MenuItem>
            ))}
          </Select>
          <Select
            size="small"
            value={selectedCompetition}
            onChange={(e) => {
              setSelectedCompetition(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="all">Todos los eventos</MenuItem>
            {availableCompetitions.map((comp) => (
              <MenuItem key={comp} value={comp}>
                {comp}
              </MenuItem>
            ))}
          </Select>
          <TextField
            size="small"
            placeholder="Buscar por jugador..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Tooltip title="Exportar lista">
            <IconButton onClick={handleExport} disabled={exporting}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <TableContainer component={Paper} elevation={0} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={(theme) => ({ backgroundColor: theme.palette.background.base })}>
              <TableCell>JUGADOR</TableCell>
              <TableCell>EVENTO / TORNEO</TableCell>
              <TableCell>COINCIDENCIAS</TableCell>
              <TableCell align="right">ACCIONES</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPlayers.map((player) => {
              const suggestions = player.suggestions || [];
              const numSuggestions = suggestions.length;

              let suggestionText = "";
              let suggestionSubtext = "";
              let suggestionReason = "";
              let suggestionColor = "";
              let SuggestionIcon = null;

              let actionText = "";
              let ActionIcon = null;
              let actionColor = "primary";

              if (numSuggestions === 1) {
                suggestionText = "1 Coincidencia Fuerte";
                suggestionSubtext = suggestions[0].name;
                suggestionReason =
                  suggestions[0].match_reason === "document"
                    ? `ID: ${suggestions[0].document_number}`
                    : `Tel: ${suggestions[0].cellphone_number}`;
                suggestionColor = "success.main";
                SuggestionIcon = CheckCircleOutlineIcon;
                actionText = "Vincular";
                ActionIcon = LinkIcon;
                actionColor = "success";
              } else if (numSuggestions > 1) {
                suggestionText = `${numSuggestions} Posibles Coincidencias`;
                suggestionSubtext = "Requiere revisión manual";
                suggestionColor = "warning.main";
                SuggestionIcon = ErrorOutlineIcon;
                actionText = "Ver coincidencias";
                ActionIcon = RemoveRedEyeIcon;
                actionColor = "inherit";
              } else {
                suggestionText = "No se encontraron coincidencias";
                suggestionSubtext = "No hay registros existentes";
                suggestionColor = "text.secondary";
                SuggestionIcon = SearchOffIcon;
                actionText = "Crear Nuevo Miembro";
                ActionIcon = PersonAddIcon;
                actionColor = "inherit";
              }

              return (
                <TableRow key={`${player.participant_id}-${player.player_id}`} hover>
                  <TableCell>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar src={player.full_profile_pic_url} sx={{ bgcolor: "grey.800", color: "white" }}>
                        {player.name ? player.name.substring(0, 2).toUpperCase() : "?"}
                      </Avatar>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body1" fontWeight="500">
                            {player.name}
                          </Typography>
                          {player.playerable_type === "App\\User" && (
                            <Chip label="REVA" color="primary" size="small" sx={{ height: 20, fontSize: "0.65rem" }} />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {player.document_number ? `ID: ${player.document_number}` : ""}
                          {player.document_number && player.cellphone_number ? " • " : ""}
                          {player.cellphone_number || ""}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    <Typography variant="body1" fontWeight="500">
                      {player.competition_name ?? "-"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {player.tournament_name ?? "-"}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      {SuggestionIcon && <SuggestionIcon sx={{ color: suggestionColor, fontSize: 20, mt: 0.3 }} />}
                      <Box>
                        <Typography variant="body2" fontWeight="500" sx={{ color: suggestionColor }}>
                          {suggestionText}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {suggestionSubtext} {suggestionReason}
                        </Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      color={actionColor}
                      startIcon={<ActionIcon />}
                      onClick={() => handleActionClick(player)}
                      sx={{ textTransform: "none", borderRadius: 2 }}
                    >
                      {actionText}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {paginatedPlayers.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No se encontraron jugadores no vinculados.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        // component="div"
        count={filteredAndSortedPlayers.length}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{ alignSelf: "flex-start" }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Filas por página"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
      />

      <AssignMemberModal
        open={modalOpen}
        onClose={handleModalClose}
        player={selectedPlayer}
        organizationId={selectedOrganization.id}
        onSave={handleMemberSave}
      />
    </Stack>
  );
};

export default UnlinkedPlayersPage;
